import type {
	API_ScheduleSessionWithRelationsResponse,
	FE_ScheduleSession,
} from "@pilatestime/shared-types";

/**
 * Convert a ScheduleSession from the backend to a FE_ScheduleSession for the frontend
 * @param session - The schedule session from the backend
 * @param enrolledCount - Number of enrolled customers
 * @returns FE_ScheduleSession formatted for FullCalendar and UI display
 */
export function convertScheduleSessionToEventSession(
	session: API_ScheduleSessionWithRelationsResponse,
	enrolledCount = 0,
): FE_ScheduleSession {
	// Create ISO date strings for FullCalendar in strict UTC to avoid timezone shifts
	const sessionDate = new Date(session.sessionDate);
	const [startHour, startMinute] = session.startTime.split(":").map(Number);
	const [endHour, endMinute] = session.endTime.split(":").map(Number);

	// Build date-times at UTC so 09:00 stays 09:00 when rendered in a UTC calendar
	const year = sessionDate.getUTCFullYear();
	const month = sessionDate.getUTCMonth();
	const day = sessionDate.getUTCDate();

	const startDateTime = new Date(
		Date.UTC(year, month, day, startHour, startMinute, 0, 0),
	);
	const endDateTime = new Date(
		Date.UTC(year, month, day, endHour, endMinute, 0, 0),
	);

	// Get staff member name from the staffMember relation
	let staffMemberName = "Unknown Staff";

	if (session.staffMember) {
		const firstName = session.staffMember.firstName || "";
		const lastName = session.staffMember.lastName || "";
		const fullName = `${firstName} ${lastName}`.trim();
		if (fullName) {
			staffMemberName = fullName;
		} else if (session.staffMember.email) {
			// Fallback to email if no name is available
			staffMemberName = session.staffMember.email;
		}
	}

	// Get establishment name from the establishment relation
	let establishmentName = "Unknown Establishment";
	if (session.establishment?.name) {
		establishmentName = session.establishment.name;
	}

	// Get staff member color from the staffMember relation (or undefined if not set)
	const staffMemberColor = session.staffMember?.color ?? undefined;

	return {
		id: session.id,
		scheduleId: session.scheduleId,
		organizationId: session.organizationId,
		establishmentId: session.establishmentId,
		staffMemberId: session.staffMemberId,
		name: session.name,
		description: session.description,
		type: session.type,
		capacity: session.capacity,
		startTime: startDateTime.toISOString(),
		endTime: endDateTime.toISOString(),
		sessionDate: sessionDate,
		isActive: session.isActive,
		isCanceled: session.isCanceled,
		notes: session.notes,
		scheduleTitle: session.name,
		staffMemberName,
		staffMemberColor,
		establishmentName,
		enrolledCustomers: enrolledCount,
		customerNames: session.customerNames,
		hasTrialAttendance: session.hasTrialAttendance,
		createdAt: session.createdAt,
		updatedAt: session.updatedAt,
	};
}

/**
 * Format session time for display in UTC
 * @param startTime - ISO string start time
 * @param endTime - ISO string end time
 * @returns Formatted time range string (e.g., "09:00 - 10:00")
 */
export function formatSessionTime(startTime: string, endTime: string): string {
	// Parse as UTC to avoid timezone shifts
	const start = new Date(startTime);
	const end = new Date(endTime);

	const formatTime = (date: Date) => {
		// Use UTC methods to format time consistently
		const hours = date.getUTCHours();
		const minutes = date.getUTCMinutes();

		// Format as HH:MM in 24-hour format to avoid timezone confusion
		return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
	};

	return `${formatTime(start)} - ${formatTime(end)}`;
}
